// File: app/src/utils/sessionRecipes.js
// Session recipes (issue #78) — a named, persisted *sparse partial* planConfig.
//
// A recipe is not a complete configuration. Every field is optional, and an
// absent key means "use my profile", including when the profile later changes.
// That is the whole point: a recipe that says only "30 minutes" must not also
// freeze the equipment, goal and warm-up settings that happened to be on screen
// when it was saved.
//
// This module is the single contract for that sparseness. Both the save flow in
// the generate dialog and the pin editor in Profile go through it, so the two
// cannot drift apart on what "pinned" means.

import { resolveSegmentMode, segmentFlagsForMode } from "./workoutPlanUtils";
import { formatLabel } from "./formatLabel";
import {
  FORMAT_OPTIONS,
  WORKOUT_TYPE_OPTIONS,
} from "../config/workoutOptions";

// Bumped only when a field's *meaning* changes — not when a field is added.
// Absent keys already handle new fields by construction, so a recipe is never
// validated against an exhaustive key list.
export const RECIPE_VERSION = 1;

// The fields a recipe may pin, in the order they are offered on save.
//
// `readiness` and `useHistoryGuidance` are deliberately absent: they are
// per-session state ("how do I feel today"), not recipe material. Warm-up and
// cool-down are single composite fields here, not one entry per stored key —
// see buildRecipeConfig.
// Display names for stored style and format values ("hiit" -> "Hard
// intervals"); legacy values with no option fall back to Title Case.
const OPTION_LABELS = Object.fromEntries(
  [...WORKOUT_TYPE_OPTIONS, ...FORMAT_OPTIONS].map((o) => [o.value, o.label]),
);
const optionLabel = (value) => OPTION_LABELS[value] || formatLabel(value);

export const RECIPE_FIELDS = [
  { key: "sessionDuration", label: "Time available" },
  { key: "workoutTypes", label: "Train for" },
  { key: "format", label: "Format" },
  { key: "primaryGoal", label: "Goal" },
  { key: "targetAreas", label: "Muscle focus" },
  { key: "trainingLocation", label: "Location" },
  { key: "availableEquipment", label: "Equipment" },
  { key: "sessionNotes", label: "Standing instruction" },
  { key: "noFaff", label: "No faff" },
  { key: "warmup", label: "Warm-up" },
  { key: "cooldown", label: "Cool-down" },
];

export const RECIPE_FIELD_KEYS = RECIPE_FIELDS.map((f) => f.key);

const SEGMENT_FIELDS = { warmup: "Warmup", cooldown: "Cooldown" };

/**
 * Value equality that treats arrays by content and order-insensitively.
 *
 * Note what this deliberately does *not* do: it never conflates an empty value
 * with an absent one. `[]` and `0` are real, meaningful settings —
 * `availableEquipment: []` means *bodyweight only*, which is a constraint a
 * recipe must be able to express.
 */
const sameValue = (a, b) => {
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    const left = [...a].sort();
    const right = [...b].sort();
    return left.every((item, i) => item === right[i]);
  }
  return a === b;
};

/**
 * Find the equipment preset whose kit list exactly matches `equipment`.
 *
 * Referencing a preset by id beats copying its list: the recipe then tracks the
 * set as the user edits it, instead of the two silently drifting apart.
 */
export const matchEquipmentPreset = (equipment, equipmentPresets) => {
  if (!Array.isArray(equipment) || !Array.isArray(equipmentPresets)) return null;
  return (
    equipmentPresets.find((preset) =>
      sameValue(
        equipment,
        Array.isArray(preset.equipment) ? preset.equipment : [],
      ),
    ) || null
  );
};

/**
 * Build the sparse config to persist, from a full planConfig plus the set of
 * fields the user chose to pin.
 *
 * Returns `{ config, equipmentPresetId }`. The equipment reference is hoisted
 * out because it is stored as a real column with ON DELETE SET NULL, so a
 * deleted equipment set degrades the recipe rather than breaking it.
 */
export const buildRecipeConfig = (
  planConfig = {},
  pinnedKeys = [],
  equipmentPresets = [],
) => {
  const pinned = new Set(pinnedKeys);
  const config = { version: RECIPE_VERSION };
  let equipmentPresetId = null;

  RECIPE_FIELD_KEYS.forEach((key) => {
    if (!pinned.has(key)) return;

    // Warm-up / cool-down: store the *mode*, never the derived autoWarmup /
    // includeWarmup pair. Those are re-expanded through segmentFlagsForMode on
    // load, so a later change to that function cannot silently break every
    // saved recipe — and a recipe pinning only the mode can never leave the
    // three keys disagreeing with each other.
    if (SEGMENT_FIELDS[key]) {
      const mode = resolveSegmentMode(planConfig, key);
      config[`${key}Mode`] = mode;
      // On "on" the exact minutes are the setting; on "auto" the AI sizes the
      // segment, so the threshold and preferred length stay profile-level.
      if (mode === "on") {
        const duration = planConfig[`${key}Duration`];
        if (duration !== undefined && duration !== null) {
          config[`${key}Duration`] = duration;
        }
      }
      return;
    }

    if (key === "availableEquipment") {
      const equipment = planConfig.availableEquipment;
      if (!Array.isArray(equipment)) return;
      const match = matchEquipmentPreset(equipment, equipmentPresets);
      if (match) {
        equipmentPresetId = match.id;
        return;
      }
      config.availableEquipment = [...equipment];
      return;
    }

    const value = planConfig[key];
    if (value === undefined || value === null) return;
    config[key] = Array.isArray(value) ? [...value] : value;
  });

  return { config, equipmentPresetId };
};

/**
 * Pre-tick the save checklist by diffing the form against the profile defaults.
 *
 * This is a suggestion, not the decision — the user confirms it on save. The
 * common case ("45 minutes and kettlebells, everything else as usual") then
 * needs one confirming tap rather than nine.
 */
export const suggestPinnedFields = (planConfig = {}, profileConfig = {}) =>
  RECIPE_FIELD_KEYS.filter((key) => {
    if (SEGMENT_FIELDS[key]) {
      return (
        resolveSegmentMode(planConfig, key) !==
        resolveSegmentMode(profileConfig, key)
      );
    }
    // Auto and absent mean the same thing for the format.
    if (key === "format") {
      return (planConfig.format || "auto") !== (profileConfig.format || "auto");
    }
    // Off and absent mean the same thing for a switch.
    if (key === "noFaff") {
      return Boolean(planConfig.noFaff) !== Boolean(profileConfig.noFaff);
    }
    return !sameValue(planConfig[key], profileConfig[key]);
  });

/**
 * Which fields a stored recipe pins — the inverse of buildRecipeConfig, used to
 * re-open the checklist when editing and to summarise a recipe in Profile.
 */
export const pinnedFieldsOf = (recipe) => {
  const config = recipe?.config || {};
  return RECIPE_FIELD_KEYS.filter((key) => {
    if (SEGMENT_FIELDS[key]) return config[`${key}Mode`] !== undefined;
    if (key === "availableEquipment") {
      return (
        config.availableEquipment !== undefined ||
        recipe?.equipment_preset_id != null
      );
    }
    return config[key] !== undefined;
  });
};

/**
 * Expand a stored recipe into `configOverrides` for the generate dialog.
 *
 * `buildInitialPlanConfig` layers these over the profile defaults and drops
 * undefined/null, so anything omitted here correctly falls through to the
 * profile. That is how a recipe stays partial all the way to generation.
 */
export const expandRecipeConfig = (recipe, equipmentPresets = []) => {
  const stored = recipe?.config || {};
  const overrides = {};

  Object.entries(stored).forEach(([key, value]) => {
    if (key === "version") return;
    if (value === undefined || value === null) return;
    overrides[key] = Array.isArray(value) ? [...value] : value;
  });

  // Re-expand the tri-state rather than trusting stored booleans.
  Object.keys(SEGMENT_FIELDS).forEach((segment) => {
    const mode = stored[`${segment}Mode`];
    if (mode === undefined || mode === null) return;
    Object.assign(overrides, segmentFlagsForMode(mode, segment));
  });

  // A recipe outlives the things it references. If the equipment set it points
  // at has been deleted, say nothing about equipment and let the profile
  // default apply — an empty kit list would generate a bodyweight session the
  // user never asked for.
  const presetId = recipe?.equipment_preset_id;
  if (presetId != null) {
    const preset = (equipmentPresets || []).find((p) => p.id === presetId);
    if (preset) {
      overrides.availableEquipment = Array.isArray(preset.equipment)
        ? [...preset.equipment]
        : [];
    } else {
      delete overrides.availableEquipment;
    }
  }

  return overrides;
};

/**
 * Human-readable summary of what a recipe pins, for the picker and the Profile
 * card. The equipment reference resolves to the preset's *name*, so a broken
 * link is something the user can see and fix rather than discover at
 * generation time.
 */
export const describeRecipe = (recipe, equipmentPresets = []) => {
  const config = recipe?.config || {};
  const parts = [];

  if (config.sessionDuration !== undefined) {
    parts.push(`${config.sessionDuration} min`);
  }
  if (Array.isArray(config.workoutTypes) && config.workoutTypes.length) {
    parts.push(config.workoutTypes.map(optionLabel).join(", "));
  }
  if (config.format && config.format !== "auto") {
    parts.push(optionLabel(config.format));
  }
  if (config.primaryGoal) parts.push(config.primaryGoal);
  if (Array.isArray(config.targetAreas) && config.targetAreas.length) {
    parts.push(config.targetAreas.join(", "));
  }

  const presetId = recipe?.equipment_preset_id;
  if (presetId != null) {
    const preset = (equipmentPresets || []).find((p) => p.id === presetId);
    parts.push(preset ? preset.name : "Equipment set deleted");
  } else if (Array.isArray(config.availableEquipment)) {
    parts.push(
      config.availableEquipment.length
        ? config.availableEquipment.join(", ")
        : "Bodyweight only",
    );
  }

  if (config.trainingLocation) parts.push(config.trainingLocation);
  if (config.noFaff) parts.push("No faff");
  if (config.warmupMode) parts.push(`Warm-up ${config.warmupMode}`);
  if (config.cooldownMode) parts.push(`Cool-down ${config.cooldownMode}`);

  return parts;
};

/**
 * True when the recipe references an equipment set that no longer exists.
 */
export const hasBrokenEquipmentLink = (recipe, equipmentPresets = []) => {
  const presetId = recipe?.equipment_preset_id;
  if (presetId == null) return false;
  return !(equipmentPresets || []).some((p) => p.id === presetId);
};
